/**
 * BuildInfo component - displays build information (hash, timestamp, environment)
 * Only visible in dev environment
 */

import { useEffect, useState } from 'react';

interface BuildInfo {
  buildHash: string;
  buildTime: string;
  environment: string;
  apiEndpoint: string;
}

// Helper function to log build info
const logBuildInfo = (info: BuildInfo) => {
  console.log(
    '%c🔨 Build Info',
    'color: #fbbf24; font-weight: bold; font-size: 14px;'
  );
  console.log(
    `%cBuild Hash: %c${info.buildHash}`,
    'color: #9ca3af;',
    'color: #fbbf24; font-weight: bold;'
  );
  console.log(
    `%cEnvironment: %c${info.environment}`,
    'color: #9ca3af;',
    'color: #60a5fa; font-weight: bold;'
  );
  console.log(
    `%cAPI Endpoint: %c${info.apiEndpoint}`,
    'color: #9ca3af;',
    'color: #34d399; font-weight: bold;'
  );
  console.log(
    `%cBuild Time: %c${new Date(info.buildTime).toLocaleString()}`,
    'color: #9ca3af;',
    'color: #d1d5db;'
  );
  console.log('─'.repeat(50));
};

export default function BuildInfo() {
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Only show in dev environment (app-dev.runow.ai or localhost)
    const hostname = window.location.hostname;
    const isDev = hostname.includes('app-dev.runow.ai') || 
                  hostname.includes('localhost') || 
                  hostname.includes('127.0.0.1') ||
                  hostname.includes('.cloudfront.net'); // CloudFront URLs for dev

    if (!isDev) {
      return; // Don't show in production
    }

    // Check if we're in Vite dev mode (local development server)
    const isViteDev = import.meta.env.DEV;
    
    // Function to extract build hash from script tags (only works for built bundles)
    const extractBuildHash = (): string => {
      // Try to find script tag with build hash
      const scripts = document.querySelectorAll('script[src*="/assets/index-"], script[src*="index-"]');
      
      for (const script of Array.from(scripts)) {
        const src = script.getAttribute('src') || '';
        const match = src.match(/index-([a-zA-Z0-9]+)\.js/);
        if (match) {
          return match[1];
        }
      }
      
      // Fallback: try to find from any script tag
      const allScripts = document.querySelectorAll('script[src]');
      for (const script of Array.from(allScripts)) {
        const src = script.getAttribute('src') || '';
        const match = src.match(/index-([a-zA-Z0-9]+)\.js/);
        if (match) {
          return match[1];
        }
      }
      
      return 'unknown';
    };

    // In Vite dev mode, there's no build hash (source files are served directly)
    let buildHash: string;
    if (isViteDev) {
      buildHash = 'dev-local';
    } else {
      // Try to extract build hash from script tags (for deployed builds)
      buildHash = extractBuildHash();
      
      // If not found, wait a bit for scripts to load and try again
      if (buildHash === 'unknown') {
        const timeout = setTimeout(() => {
          buildHash = extractBuildHash();
          if (buildHash !== 'unknown') {
            // Re-run the effect logic with the found hash
            const buildTimeMeta = document.querySelector('meta[name="build-time"]');
            const buildTime = buildTimeMeta?.getAttribute('content') || new Date().toISOString();
            const environment = import.meta.env.MODE || 'unknown';
            const apiEndpoint = import.meta.env.VITE_API_ENDPOINT || 'not configured';
            
            const info = {
              buildHash,
              buildTime,
              environment,
              apiEndpoint,
            };
            
            setBuildInfo(info);
            setIsVisible(true);
            logBuildInfo(info);
          }
        }, 100);
        
        return () => clearTimeout(timeout);
      }
    }

    // Get build time from meta tag or use current time as fallback
    const buildTimeMeta = document.querySelector('meta[name="build-time"]');
    const buildTime = buildTimeMeta?.getAttribute('content') || new Date().toISOString();

    // Get environment info
    const environment = import.meta.env.MODE || 'unknown';
    const apiEndpoint = import.meta.env.VITE_API_ENDPOINT || 'not configured';

    const info = {
      buildHash,
      buildTime,
      environment,
      apiEndpoint,
    };

    setBuildInfo(info);
    setIsVisible(true);
    logBuildInfo(info);
  }, []);

  if (!isVisible || !buildInfo) {
    return null;
  }

  return (
    <div className="fixed bottom-0 right-0 z-50 p-2 text-xs font-mono bg-black/80 text-white rounded-tl-lg border-t border-l border-gray-600">
      <div className="flex flex-col gap-1">
        <div>
          <span className="text-gray-400">Build:</span>{' '}
          <span className="text-yellow-400 font-bold">{buildInfo.buildHash.substring(0, 8)}</span>
        </div>
        <div>
          <span className="text-gray-400">Env:</span>{' '}
          <span className="text-blue-400">{buildInfo.environment}</span>
        </div>
        <div>
          <span className="text-gray-400">API:</span>{' '}
          <span className="text-green-400 text-[10px] break-all max-w-[200px]">{buildInfo.apiEndpoint}</span>
        </div>
        <div>
          <span className="text-gray-400">Time:</span>{' '}
          <span className="text-gray-300 text-[10px]">
            {new Date(buildInfo.buildTime).toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}
